import { Layout } from "@/components/Layout";
import { SeoHead } from "@/components/SeoHead";
import { Phone, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PHONE_HREF, PHONE_NUMBER } from "@/components/EmergencyBanner";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const PRIMARY_RECIPIENT = "joby@ptd.net";

const Contact = () => {
  const [formData, setFormData] = useState({ name: "", phone: "", email: "", message: "", service: "general" });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const serviceLabels: Record<string, string> = {
        general: "General Inquiry",
        "well-pump-repair": "Well Pump Repair",
        "well-pump-installation": "Well Pump Installation",
        "grinder-pump": "Grinder Pump Service",
        "sewage-pump": "Sewage Pump Service",
        "water-filtration": "Water Filtration",
        other: "Other",
      };

      const submissionId = crypto.randomUUID();
      const serviceLabel = serviceLabels[formData.service] || formData.service;
      const submittedAt = new Date().toLocaleString("en-US", { timeZone: "America/New_York" }) + " ET";

      // Owner notification
      await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "quote-request-notification",
          recipientEmail: PRIMARY_RECIPIENT,
          idempotencyKey: `quote-notify-${submissionId}`,
          templateData: {
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            service: serviceLabel,
            message: formData.message,
            submittedAt,
          },
        },
      });

      // Customer confirmation
      await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "quote-request-confirmation",
          recipientEmail: formData.email,
          idempotencyKey: `quote-confirm-${submissionId}`,
          templateData: {
            name: formData.name,
            service: serviceLabel,
            message: formData.message,
          },
        },
      });

      navigate("/thank-you");
    } catch {
      toast({ title: "Error", description: "Something went wrong. Please call us directly.", variant: "destructive" });
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <SeoHead
        title="Contact Us for Well Pump Service | Semper Fi Pump Service"
        description="Contact Semper Fi for well pump repair, installation, or emergency service in the Pocono Mountains. Call now or submit a service request online."
      />
      <section className="bg-primary">
        <div className="container py-16 md:py-24">
          <h1 className="text-3xl md:text-4xl font-black text-primary-foreground mb-4 border-l-0 pl-0">Contact Us</h1>
          <p className="text-primary-foreground/80 max-w-2xl text-pretty">
            For emergencies, call us directly. For non-urgent service requests, use the form below.
          </p>
        </div>
      </section>

      <section className="container py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <h2 className="text-2xl font-black mb-6">Get In Touch</h2>
            <div className="space-y-6 mb-10">
              <a href={PHONE_HREF} className="flex items-start gap-4 group">
                <div className="w-10 h-10 rounded-sm bg-accent/10 flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="font-semibold text-foreground group-hover:text-accent transition-colors">{PHONE_NUMBER}</p>
                  <p className="text-sm text-muted-foreground">Call for immediate service</p>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-sm bg-primary/5 flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">119 Lacey Ln, Saylorsburg, PA 18353</p>
                  <p className="text-sm text-muted-foreground">Serving a 60-mile radius</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-sm bg-primary/5 flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">Emergency Service Available</p>
                  <p className="text-sm text-muted-foreground">Same-day response for no-water calls</p>
                </div>
              </div>
            </div>

            <div className="bg-accent/5 rounded-sm p-6 border border-accent/20">
              <h3 className="font-bold mb-2 border-l-0 pl-0 text-accent">Emergency?</h3>
              <p className="text-sm text-muted-foreground mb-4">If you have no water or a sewage emergency, don't fill out a form. Call us now.</p>
              <Button asChild variant="emergency" size="lg">
                <a href={PHONE_HREF}><Phone className="w-4 h-4" />Call Now</a>
              </Button>
            </div>
          </div>

          {/* Form */}
          <div className="bg-card rounded-sm p-8 shadow-card">
            <h2 className="text-xl font-bold mb-6 border-l-0 pl-0">Request Service</h2>
              <form id="contact-form" name="service-request" action="/thank-you" onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5">Name *</label>
                  <input
                    type="text" required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full rounded-sm border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">Phone *</label>
                  <input
                    type="tel" required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full rounded-sm border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">Email *</label>
                  <input
                    type="email" required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full rounded-sm border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">Service Needed</label>
                  <select
                    value={formData.service}
                    onChange={(e) => setFormData({...formData, service: e.target.value})}
                    className="w-full rounded-sm border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="general">General Inquiry</option>
                    <option value="well-pump-repair">Well Pump Repair</option>
                    <option value="well-pump-installation">Well Pump Installation</option>
                    <option value="grinder-pump">Grinder Pump Service</option>
                    <option value="sewage-pump">Sewage Pump Service</option>
                    <option value="water-filtration">Water Filtration</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">Describe Your Issue *</label>
                  <textarea
                    rows={4}
                    required
                    minLength={10}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full rounded-sm border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                    placeholder="Tell us what's going on with your water system..."
                  />
                </div>
                <Button type="submit" className="w-full" size="lg" disabled={submitting}>
                  {submitting ? "Sending..." : "Submit Service Request"}
                </Button>
              </form>
            </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
